import { useMemo } from 'react';
import { getAfaRate } from './useRscPricing';

// Solar yield — commercial sites derate from RSC 3.5.
const YIELD_PER_KWP = 3.4;        // kWh per kWp per day, MY commercial
const DAYS_PER_MONTH = 30;
const WORKWEEKS_PER_MONTH = 4.345;
const SMP_RATE = 0.20;            // RM/kWh sellback
const PANEL_KWP = 0.64;           // 640Wp per panel

// Safety ratio — kWp never exceeds inverter × 1.35 (battery or not).
export const RATIO_CAP_NO_BATT = 1.35;

// Solar generation window — daily kWh × 3.4 is assumed to be produced
// flat across this 10-hour band. Hours outside the band have no generation.
const SOLAR_START_HR = 8;
const SOLAR_END_HR = 18;
const SOLAR_HOURS_PER_DAY = SOLAR_END_HR - SOLAR_START_HR;   // 10

// ── TNB commercial LV tariff: "Bukan Domestik Am Voltan Rendah" ──────────────
// New tariff structure effective 1 July 2025, verified line-for-line against a
// real May-2026 bill (210492234600): 9,599 kWh →
//   Energy   0.2703/kWh = 2,594.61   Capacity 0.0883/kWh = 847.59
//   Network  0.1482/kWh = 1,422.57   Retail   RM20 flat   = 20.00
//   KWTBB 1.6% of (energy+capacity+network) = 77.84
//   Caj Semasa (excl AFA) = 4,962.61   (bill's 5,035.88 includes AFA + rounding)
// AFA (Automatic Fuel Adjustment) is intentionally EXCLUDED — it changes monthly
// and we want a stable estimate (same approach as the RSC residential calc).
// No Service Tax (ST) applies at this LV-commercial tier.
const LV_ENERGY_RATE   = 0.2703;
const LV_CAPACITY_RATE = 0.0883;
const LV_NETWORK_RATE  = 0.1482;
const LV_RETAIL_FLAT   = 20;
const LV_KWTBB_RATE    = 0.016;

// Returns the itemised LV-commercial bill for a monthly kWh. Mirrors the RSC
// calcC2Bill shape so the Combined card can render the 原电费/新电费 columns.
export function calcCommercialLvBill(kwh) {
    const k = Math.max(0, Number(kwh) || 0);
    const energy = k * LV_ENERGY_RATE;
    const capacity = k * LV_CAPACITY_RATE;
    const network = k * LV_NETWORK_RATE;
    const retail = k > 0 ? LV_RETAIL_FLAT : 0;
    // AFA (Automatic Fuel Adjustment) — same rate as the system (sen/kWh from General
    // Settings); negative = rebate. In the bill subtotal but EXCLUDED from the KWTBB base.
    const afa = k * getAfaRate() / 100;
    const kwtbb = (energy + capacity + network) * LV_KWTBB_RATE;
    const total = energy + capacity + network + retail + afa + kwtbb;
    return { kwh: k, energy, capacity, network, retail, afa, kwtbb, total };
}
// Effective average RM/kWh under the LV tariff (used to drive saving math so the
// card breakdown and the saving figure share ONE source of truth).
export function commercialLvRatePerKwh(kwh) {
    const k = Math.max(0, Number(kwh) || 0);
    if (k <= 0) return LV_ENERGY_RATE + LV_CAPACITY_RATE + LV_NETWORK_RATE;
    return calcCommercialLvBill(k).total / k;
}

// Commercial pricing tiers (2026-06 list). Solis REMOVED. Brands: goodwe | huawei.
// type: 'string' | 'hybrid'. Battery systems must be Hybrid (GoodWe only).
// - goodwe: String (all tiers) + Hybrid (<100 only — ≥100 falls back to String price).
//   <26 kWp uses the residential row as-is.
// - huawei: String only, ≥100 kWp only.
const COMMERCIAL_PRICING = {
    goodwe: {
        residentialDiscountPerKwp: 0,
        string: [
            { maxKwp: 30,    pricePerKwp: 1925 },
            { maxKwp: 99.99, pricePerKwp: 1750 },
            { maxKwp: 400,   pricePerKwp: 1700 },
            { maxKwp: Infinity, pricePerKwp: 1600 },
        ],
        hybrid: [
            { maxKwp: 30,    pricePerKwp: 1975 },
            { maxKwp: 99.99, pricePerKwp: 1800 },
            { maxKwp: 400,   pricePerKwp: 1700 },   // no hybrid tier ≥100 → String price
            { maxKwp: Infinity, pricePerKwp: 1600 },
        ],
    },
    huawei: {
        // String only, ≥100 kWp only (getCommercialPrice returns 0 below 100).
        string: [
            { maxKwp: 400,      pricePerKwp: 1800 },
            { maxKwp: Infinity, pricePerKwp: 1700 },
        ],
    },
};

// Auto-Huawei rule.
const HUAWEI_THRESHOLD_KWP = 100;

// Systems with inverter capacity above 72 kW require a Suruhanjaya Tenaga
// (ST) license. Approximate fee RM 700–900/month — using a midpoint of 800.
// Applies to BOTH ATAP and SELCo whenever the candidate's inverter exceeds
// the threshold. ATAP is NOT hard-capped at 72 kW — the inverter still
// follows Max Demand × ratio cap. The threshold is purely a billing/licensing
// switch that deducts the monthly fee from savings (and surfaces ROI
// inclusive + exclusive of the fee on the card).
const ST_LICENSE_THRESHOLD_KW = 72;
const ST_LICENSE_MONTHLY_FEE = 800;

// ST-license-aware inverter ceiling (per user): never propose an inverter in the
// 72–100 kW "dead zone" — it triggers the ST license fee for little extra value.
// Cap such systems at 72 kW (the license-free maximum). Only genuine ≥100 kW
// plants go above. buildCandidate then drops any kWp that would exceed the ratio
// cap on the capped inverter, so the 72→100 kW range is skipped entirely.
// Break-free at 90 kW (user 2026-06-24): a marginal overshoot into 73–89 kW isn't
// worth the bigger system, so cap those to 72. But ≥ 90 kW means the bill genuinely
// needs it → propose the REAL inverter (90/95/…), so big commercial bills aren't
// throttled down to ~96 kWp. No ST license fee is charged on these (per user).
const ST_BREAKFREE_KW = 90;
function capStInverter(kw) {
    return (kw > ST_LICENSE_THRESHOLD_KW && kw < ST_BREAKFREE_KW) ? ST_LICENSE_THRESHOLD_KW : kw;
}

export const BRAND_LABEL = { solis: 'Solis', goodwe: 'GoodWe', huawei: 'Huawei' };
export const BRAND_TYPE  = { solis: 'String', goodwe: 'Hybrid', huawei: 'String' };

/**
 * Pick a commercial inverter size from Max Demand by rounding DOWN to the
 * nearest 5 kW step (real-world commercial inverter SKUs are 5/10/15/20/25/30...).
 * Examples: MD 24 → 20 kW, MD 26 → 25 kW, MD 30 → 30 kW, MD 75 → 75 kW.
 */
export function inverterFromMd(maxDemandKw) {
    return capStInverter(Math.max(5, Math.floor(maxDemandKw / 5) * 5));
}

/**
 * Compute total system price.
 *
 * For kWp < 26 with solis/goodwe: starts from the matched Residential Pricing
 * row's cash and subtracts the brand's residentialDiscountPerKwp × kwp.
 * For kWp ≥ 26 (or any kwp with Huawei): uses tier rate × kwp.
 */
function getCommercialPrice({ kwp, brand, type = 'string', residentialCash, is3p }) {
    const conf = COMMERCIAL_PRICING[brand];
    if (!conf) return 0;

    if (kwp < 26 && brand !== 'huawei') {
        if (!residentialCash) return 0;
        return Math.round(residentialCash - (conf.residentialDiscountPerKwp || 0) * kwp);
    }
    // Huawei is ≥100 kWp only.
    if (brand === 'huawei' && kwp < HUAWEI_THRESHOLD_KWP) return 0;

    const tiers = conf[type] || conf.string;
    const tier = tiers.find(t => kwp <= t.maxKwp) || tiers[tiers.length - 1];
    return Math.round(kwp * tier.pricePerKwp);
}

/**
 * Time-window overlap workday energy flow.
 *
 * Compares the operating hours window vs the solar window (8am–6pm).
 *   - overlap hours → solar lands during factory hours → self-consume
 *     (cap by daily consumption to handle the rare oversized case)
 *   - non-overlap solar hours (sun up, factory closed) → 100% export at SMP
 *   - non-solar hours → no generation
 *
 * Returns { self, exported, dailyGen, overlapHours, selfFrac }.
 */
function workdayFlow({ kwp, dailyConsumption, opStart, opEnd }) {
    const dailyGen = kwp * YIELD_PER_KWP;

    const overlapStart = Math.max(opStart, SOLAR_START_HR);
    const overlapEnd = Math.min(opEnd, SOLAR_END_HR);
    const overlapHours = Math.max(0, overlapEnd - overlapStart);
    const selfFrac = SOLAR_HOURS_PER_DAY > 0 ? overlapHours / SOLAR_HOURS_PER_DAY : 0;

    const solarInOverlap = dailyGen * selfFrac;
    const solarOutsideOp = dailyGen - solarInOverlap;

    // Normal commercial: load is well above solar, so all overlap is self.
    // The min() guards the rare oversized case where solar > daily consumption.
    const self = Math.min(solarInOverlap, dailyConsumption);
    const overlapExport = solarInOverlap - self;

    return {
        self,
        exported: solarOutsideOp + overlapExport,
        dailyGen,
        overlapHours,
        selfFrac,
    };
}

/**
 * Monthly aggregate using the bell-curve workday split. NEM mode — export
 * to grid pays SMP. Off-day generation also exports at SMP.
 */
export function estimateMonthlySaving({ kwp, tariffRate, monthlyKwh, operatingDays, opStart, opEnd, battKwhTotal = 0, offHourBaselinePct = 0.2 }) {
    const workdaysPerMonth = operatingDays * WORKWEEKS_PER_MONTH;
    const offdaysPerMonth = (7 - operatingDays) * WORKWEEKS_PER_MONTH;
    // Split monthly consumption into op-hour vs off-hour. Solar can only cover
    // op-hour consumption directly — the off-hour baseline (overnight + weekend
    // standby) is grid-imported.
    const baseline = Math.min(0.5, Math.max(0, offHourBaselinePct));
    const monthlyOpConsumption = monthlyKwh * (1 - baseline);
    const dailyConsumption = workdaysPerMonth > 0 ? monthlyOpConsumption / workdaysPerMonth : 0;

    const work = workdayFlow({ kwp, dailyConsumption, opStart, opEnd });
    const battStorageDaily = battKwhTotal * 0.9 * 0.9;
    const battAbsorbed = Math.min(work.exported, battStorageDaily);
    const workdayExport = work.exported - battAbsorbed;

    const monthGenerated = work.dailyGen * DAYS_PER_MONTH;
    // The battery shifts stored daytime export to cover the OFF-HOUR (night/weekend)
    // baseline, so its self-use contribution is capped at that off-hour load. Without
    // this cap, an oversized system + battery pushed self-use ABOVE actual consumption,
    // inflating the "Original" bill and making the saving exceed the real bill.
    const offHourConsumption = monthlyKwh * baseline;
    const monthBattSelf = Math.min(workdaysPerMonth * battAbsorbed, offHourConsumption);
    const monthSelf = workdaysPerMonth * work.self + monthBattSelf;
    const monthExportedRaw = workdaysPerMonth * workdayExport + offdaysPerMonth * work.dailyGen;

    // NEM/ATAP rule: export credit caps at the kWh the customer still needs
    // to draw from grid after self-consumption (= monthlyKwh − monthSelf).
    // Anything beyond that is generated but can't be paid for in this month.
    const monthImportAfterSolar = Math.max(0, monthlyKwh - monthSelf);
    const monthExported = Math.min(monthExportedRaw, monthImportAfterSolar);
    const monthExportLost = monthExportedRaw - monthExported;

    const originalSaving = monthGenerated * tariffRate;
    const monthlySaving = monthSelf * tariffRate + monthExported * SMP_RATE;
    const exportPct = monthGenerated > 0 ? monthExported / monthGenerated : 0;
    const lostExportPct = monthGenerated > 0 ? monthExportLost / monthGenerated : 0;

    return {
        generated: monthGenerated,
        selfConsumed: monthSelf,
        importAfterSolar: monthImportAfterSolar,
        exported: monthExported,
        exportPct,
        exportedRaw: monthExportedRaw,
        exportLost: monthExportLost,
        lostExportPct,
        curtailed: 0,
        curtailedPct: 0,
        originalSaving,
        monthlySaving,
        overlapHours: work.overlapHours,
        workdaySelfPct: work.selfFrac,
        // Battery daily fill: how much of the usable storage the day's solar surplus can
        // actually charge. < 100% ⇒ the battery can't fill up (shown as a warning).
        battUsableKwh: battStorageDaily,
        battDailyChargeKwh: battAbsorbed,
        battChargePct: battStorageDaily > 0 ? Math.min(100, Math.round(battAbsorbed / battStorageDaily * 100)) : 100,
        // Raw daily solar surplus (generation − daytime self-use) available to charge a
        // battery — used to size Option B's battery to what one day can fully charge.
        dailySurplusKwh: work.exported,
        // What the battery actually CYCLES per day = charged kWh capped by the night load
        // it can discharge into. If used < charged, the battery is oversized (a bigger
        // pack charges more but can't use it — e.g. 32 kWh saving = 16 kWh saving).
        battDailyUsedKwh: workdaysPerMonth > 0 ? monthBattSelf / workdaysPerMonth : 0,
    };
}

// Bell-curve solar POWER (kW) at clock-hour t. Half-sine over the 08–18 window,
// normalised so the whole-day integral equals dailyGen (= kWp × YIELD_PER_KWP).
// Peaks at solar noon (13:00). Outside the window → 0.
function solarKwAt(kwp, t) {
    if (t < SOLAR_START_HR || t > SOLAR_END_HR) return 0;
    const dailyGen = kwp * YIELD_PER_KWP;
    const peakKw = dailyGen * Math.PI / (2 * SOLAR_HOURS_PER_DAY);   // ∫ half-sine = dailyGen
    return peakKw * Math.sin(Math.PI * (t - SOLAR_START_HR) / SOLAR_HOURS_PER_DAY);
}

// SELCo (zero-export) workday energy flow with a BELL-CURVE solar profile and an
// INSTANTANEOUS load cap. Without a battery, solar above the load at any moment
// is curtailed (can't export, can't store). A battery stores that per-slot excess
// (90% round-trip) and discharges to cover op-hour load the direct solar missed.
//   loadKW   = avg operating-hour load = dailyOpConsumption / opHours
//   selfDirect = Σ min(solar_slot, load_slot)         (instantaneous)
//   excess     = Σ (solar_slot − used)                (curtailable / batteryable)
//   battery    = min(excess, B×0.9) discharged into the unmet op-hour load
function selcoWorkdayFlow({ kwp, dailyOpConsumption, opStart, opEnd, battKwhTotal = 0 }) {
    const opHours = Math.max(0, opEnd - opStart);
    const loadKW = opHours > 0 ? dailyOpConsumption / opHours : 0;
    const STEP = 0.25;   // 15-minute resolution
    let dailyGen = 0, selfDirect = 0, excessSolar = 0;
    for (let t = SOLAR_START_HR; t < SOLAR_END_HR; t += STEP) {
        const mid = t + STEP / 2;
        const solarE = solarKwAt(kwp, mid) * STEP;             // kWh this slot
        dailyGen += solarE;
        const inOp = mid >= opStart && mid < opEnd;
        const loadE = inOp ? loadKW * STEP : 0;
        const used = Math.min(solarE, loadE);
        selfDirect += used;
        excessSolar += solarE - used;
    }
    const battUsable = (Number(battKwhTotal) || 0) * 0.9 * 0.9; // RTE × DOD (= 0.81), matches ATAP CSC + RSC
    const stored = Math.min(excessSolar, battUsable);
    const deficit = Math.max(0, loadKW * opHours - selfDirect); // op-hour load solar didn't meet
    const selfFromBatt = Math.min(stored, deficit);
    return {
        dailyGen,
        self: selfDirect + selfFromBatt,
        selfDirect,
        selfFromBatt,
        stored,
        battUsable,
        excessSolar,
        curtailed: Math.max(0, excessSolar - selfFromBatt),
        loadKW,
    };
}

/**
 * Monthly aggregate for SELCo (Self-Consumption Only) mode — zero export.
 * Uses a bell-curve solar profile + instantaneous load cap (solar above the
 * load at any moment is curtailed without a battery). A battery stores the
 * per-slot excess and discharges into unmet op-hour load. Off-day generation
 * is fully curtailed (no op load; battery not cycled on off-days).
 * Saving = self-consumed kWh × tariff only.
 */
export function estimateMonthlySavingSELCo({ kwp, tariffRate, monthlyKwh, operatingDays, opStart, opEnd, battKwhTotal = 0, offHourBaselinePct = 0.2 }) {
    const workdaysPerMonth = operatingDays * WORKWEEKS_PER_MONTH;
    const offdaysPerMonth = (7 - operatingDays) * WORKWEEKS_PER_MONTH;
    const baseline = Math.min(0.5, Math.max(0, offHourBaselinePct));
    const monthlyOpConsumption = monthlyKwh * (1 - baseline);
    const dailyOpConsumption = workdaysPerMonth > 0 ? monthlyOpConsumption / workdaysPerMonth : 0;

    const flow = selcoWorkdayFlow({ kwp, dailyOpConsumption, opStart, opEnd, battKwhTotal });

    const monthGenerated = flow.dailyGen * DAYS_PER_MONTH;
    const monthSelf = workdaysPerMonth * flow.self;
    const monthCurtailed = Math.max(0, monthGenerated - monthSelf);
    const monthImportAfterSolar = Math.max(0, monthlyKwh - monthSelf);

    const originalSaving = monthGenerated * tariffRate;
    const monthlySaving = monthSelf * tariffRate;  // zero export revenue

    return {
        generated: monthGenerated,
        selfConsumed: monthSelf,
        importAfterSolar: monthImportAfterSolar,
        exported: 0,
        exportPct: 0,
        curtailed: monthCurtailed,
        curtailedPct: monthGenerated > 0 ? monthCurtailed / monthGenerated : 0,
        originalSaving,
        monthlySaving,
        overlapHours: Math.max(0, Math.min(opEnd, SOLAR_END_HR) - Math.max(opStart, SOLAR_START_HR)),
        offdaysPerMonth,
        // Battery daily charge/use — so the card can write it in SELCo mode too
        // (mirrors the ATAP estimateMonthlySaving fields).
        battUsableKwh: flow.battUsable,
        battDailyChargeKwh: flow.stored,
        battDailyUsedKwh: flow.selfFromBatt,
        battChargePct: flow.battUsable > 0 ? Math.min(100, Math.round(flow.stored / flow.battUsable * 100)) : 100,
        dailySurplusKwh: flow.excessSolar,
    };
}

/**
 * Right-size an inverter from kWp using the same ratio cap as residential.
 * Rounded UP to a 5 kW SKU step so panels never exceed the ratio.
 */
export function inverterFromKwp(kwp, ratioCap) {
    return capStInverter(Math.max(5, Math.ceil(kwp / ratioCap / 5) * 5));
}

/**
 * Math-derived ideal sizing — bill ÷ daily-op-kWh ÷ 3.4 ÷ 0.64, capped by MD.
 */
export function deriveSizing({ monthlyBill, tariffRate, operatingDays, maxDemandKw, battKwhTotal = 0, phaseMode = 'single', mode = 'atap' }) {
    const monthlyKwh = tariffRate > 0 ? monthlyBill / tariffRate : 0;
    const workdaysPerMonth = operatingDays * WORKWEEKS_PER_MONTH;
    const dailyKwh = workdaysPerMonth > 0 ? monthlyKwh / workdaysPerMonth : 0;
    const requiredKwp = dailyKwh / YIELD_PER_KWP;
    const requiredPanels = Math.ceil(requiredKwp / PANEL_KWP);

    // Safety ratio fixed at 1.35 (kWp ≤ inverter × 1.35) regardless of battery.
    const ratioCap = RATIO_CAP_NO_BATT;
    const is1p = phaseMode !== '3phase';
    const hasMd = Number(maxDemandKw) > 0;
    const isAtap = mode !== 'selco';

    // Inverter sizing rules:
    // - Single phase: HARD cap at 5 kW (SEDA 1P limit) regardless of mode/MD/bill.
    //   A bigger need just gets capped (UI warns to switch to 3-phase).
    // - 3-phase ATAP WITH Max Demand: inverter from MD (rounded down to 5 kW SKU) —
    //   export validated against demand.
    // - 3-phase ATAP without MD, OR SELCo (any): size from the bill-derived kWp
    //   (no MD ceiling). SELCo never uses MD (zero export).
    let inverterKw;
    if (is1p) {
        inverterKw = 5;
    } else if (isAtap && hasMd) {
        inverterKw = inverterFromMd(maxDemandKw);
    } else {
        inverterKw = inverterFromKwp(requiredKwp, ratioCap);
    }
    const maxKwpByInv = inverterKw * ratioCap;
    const maxPanelsByInv = Math.floor(maxKwpByInv / PANEL_KWP);

    const targetPanels = Math.min(requiredPanels, maxPanelsByInv);
    return {
        monthlyKwh,
        workdaysPerMonth,
        dailyKwh,
        requiredKwp,
        requiredPanels,
        inverterKw,
        maxKwpByInv,
        maxPanelsByInv,
        targetPanels,
        ratioCap,
        is1p,
        hasMd,
        // True when single-phase can't fit the bill within the 5 kW ceiling →
        // UI shows "needs 3-phase" warning.
        single1pCapped: is1p && requiredPanels > maxPanelsByInv,
    };
}

/**
 * Determine the effective brand at a given kWp. Forces Huawei at ≥100 kWp.
 */
export function resolveBrand(userBrand, kwp) {
    const b = (userBrand || 'goodwe').toLowerCase();
    if (b === 'solis') return 'goodwe';                                   // Solis removed
    if (b === 'huawei' && kwp < HUAWEI_THRESHOLD_KWP) return 'goodwe';    // Huawei N/A <100 kWp
    return b;   // GoodWe valid at every kWp (incl ≥100); Huawei kept ≥100
}

// Resolve the inverter type. Battery requires Hybrid; Huawei is String-only.
export function resolveType(userType, brand, battKwhTotal = 0) {
    if ((brand || 'goodwe').toLowerCase() === 'huawei') return 'string';
    if (Number(battKwhTotal) > 0) return 'hybrid';
    return (userType || 'string').toLowerCase() === 'hybrid' ? 'hybrid' : 'string';
}

function buildCandidate({ panels, brand, invType = 'string', is3p, residentialRows, monthlyBill, tariffRate, operatingDays, opStart, opEnd, battKwhTotal, breakdown, mode = 'nem', offHourBaselinePct = 0.2, inverterKwOverride = 0, fullSelfUse = false }) {
    const kwp = Math.round(panels * PANEL_KWP * 100) / 100;
    const effectiveBrand = resolveBrand(brand, kwp);
    const effectiveType = resolveType(invType, effectiveBrand, battKwhTotal);

    let residentialCash = 0;
    if (kwp < 26 && residentialRows?.length) {
        const row = residentialRows.find(r => r.panels === panels);
        if (row) residentialCash = is3p ? row.cash3p : row.cash;
    }

    const cashPrice = getCommercialPrice({ kwp, brand: effectiveBrand, type: effectiveType, residentialCash, is3p });
    if (!cashPrice) return null;

    const priceBreakdown = (() => {
        if (kwp < 26 && effectiveBrand !== 'huawei') {
            const discountPerKwp = COMMERCIAL_PRICING[effectiveBrand].residentialDiscountPerKwp || 0;
            return {
                basis: 'residential',
                residentialCash,
                discountPerKwp,
                discountTotal: Math.round(discountPerKwp * kwp),
                finalPrice: cashPrice,
            };
        }
        const conf = COMMERCIAL_PRICING[effectiveBrand];
        const tiers = conf[effectiveType] || conf.string;
        const tier = tiers.find(t => kwp <= t.maxKwp) || tiers[tiers.length - 1];
        const tierLabel = kwp < 30 ? '26–30 kWp' : (kwp < 100 ? '30–<100 kWp' : (kwp <= 400 ? '100–400 kWp' : '>400 kWp'));
        return { basis: 'tier', ratePerKwp: tier.pricePerKwp, tierLabel, finalPrice: cashPrice };
    })();

    let energy = mode === 'selco'
        ? estimateMonthlySavingSELCo({ kwp, tariffRate, monthlyKwh: breakdown.monthlyKwh, operatingDays, opStart, opEnd, battKwhTotal, offHourBaselinePct })
        : estimateMonthlySaving({ kwp, tariffRate, monthlyKwh: breakdown.monthlyKwh, operatingDays, opStart, opEnd, battKwhTotal, offHourBaselinePct });

    // "100% self-use" override — ignore export & curtailment: treat all generated
    // kWh as offsetting the bill, capped at actual consumption. Gives the
    // optimistic full-offset saving (final cap to the bill happens below).
    if (fullSelfUse) {
        // Scale by operating days: generation happens 7 days/week but is only self-used
        // on operating days (non-op days curtail — SELCo has no export). So 100% daytime
        // still loses (7 − operatingDays)/7 of the generation. This is why fewer operating
        // days now give a LOWER saving (user 2026-06-24) instead of a flat full-offset.
        const _opFrac = Math.min(1, (operatingDays || 7) / 7);
        const selfFull = Math.min(energy.generated * _opFrac, breakdown.monthlyKwh);
        energy = {
            ...energy,
            selfConsumed: selfFull,
            curtailed: 0,
            curtailedPct: 0,
            exported: 0,
            exportPct: 0,
            importAfterSolar: Math.max(0, breakdown.monthlyKwh - selfFull),
            monthlySaving: selfFull * tariffRate,
        };
    }

    // Right-size inverter to the candidate's actual kWp. Both ATAP and SELCo
    // can cross the 72 kW threshold (which triggers the ST License fee but
    // doesn't cap the system size). For ATAP the iteration is bounded by
    // breakdown.maxPanelsByInv (= MD × ratio); SELCo has no MD ceiling.
    const inverterKw = inverterKwOverride > 0 ? inverterKwOverride : inverterFromKwp(kwp, breakdown.ratioCap);

    // If the inverter got capped into the license-free zone (72 kW) but this kWp
    // needs more, the ratio would exceed the cap — drop the candidate. This carves
    // out the 72→100 kW gap so nothing is proposed in that range (per user: cap at
    // 72 kW, jump straight to ≥100 kW for genuine large plants).
    if (inverterKwOverride <= 0 && kwp > inverterKw * breakdown.ratioCap + 0.001) return null;

    // ST License applies above 72 kW inverter — flat ~RM 800/month operating
    // expense that eats into the customer's net savings.
    const stLicenseApplies = inverterKw > ST_LICENSE_THRESHOLD_KW;
    // No ST license fee charged on break-free systems (user 2026-06-24) — flag kept
    // for display only; it never reduces the savings/ROI.
    const monthlyLicenseFee = 0;

    // Customer can never save more than 100% of their actual bill — TNB credits
    // for excess export top out at the bill total in practical terms (minimum
    // service charge ≈ RM 7 stays). Cap the gross saving first.
    const grossMonthlySaving = monthlyBill > 0
        ? Math.min(energy.monthlySaving, monthlyBill)
        : energy.monthlySaving;
    const savingCapped = grossMonthlySaving < energy.monthlySaving;

    // Net monthly saving = gross minus ST License fee (floor at 0). Used for
    // % of Bill Saved, ROI, and the long-horizon projections.
    const netMonthlySaving = Math.max(0, grossMonthlySaving - monthlyLicenseFee);

    const savingPct = monthlyBill > 0 ? netMonthlySaving / monthlyBill : 0;
    const roiYears = netMonthlySaving > 0 ? cashPrice / (netMonthlySaving * 12) : null;
    const roiYearsGross = grossMonthlySaving > 0 ? cashPrice / (grossMonthlySaving * 12) : null;

    return {
        kwp,
        panels,
        inverterKw,
        cashPrice,
        ratio: Math.round((kwp / inverterKw) * 100) / 100,
        savingPct,
        roiYears: roiYears != null ? Math.round(roiYears * 100) / 100 : null,
        roiYearsGross: roiYearsGross != null ? Math.round(roiYearsGross * 100) / 100 : null,
        brand: effectiveBrand,
        brandLabel: BRAND_LABEL[effectiveBrand],
        invType: effectiveType,
        brandType: effectiveType === 'hybrid' ? 'Hybrid' : 'String',
        priceTier: kwp < 26 ? 'residential' : (kwp < 30 ? '26–30' : (kwp < 100 ? '<100' : (kwp < 400 ? '100–400' : '>400'))),
        priceBreakdown,
        mode,
        savingCapped,
        stLicenseApplies,
        monthlyLicenseFee,
        grossMonthlySaving,
        ...energy,
        monthlySaving: netMonthlySaving,
    };
}

/**
 * Three-proposal CSC Bill picker.
 *
 * NEM mode — iterates panel counts from 1 to maxPanelsByInv (= MD × ratio cap):
 *   - maxSaving: highest monthly saving (typically MD-capped top end)
 *   - fastestRoi: lowest payback among candidates with savingPct ≥ 0.5
 *
 * SELCo mode — sized to match workday op-hour consumption (no MD cap, no
 * export revenue). Useful when MD constrains NEM but the customer wants more
 * coverage via the simpler zero-export approval path.
 *
 * Any of the three can be null:
 *   - fastestRoi: no candidate hits the 50% floor, or it would dedupe maxSaving
 *   - selco: no solar/op-hour overlap (24h ops with op outside the solar window)
 */
export default function useCscBillProposal({
    residentialRows,
    monthlyBill,
    tariffRate,
    operatingDays,
    opStart = 9,
    opEnd = 17,
    maxDemandKw,
    battKwhTotal = 0,
    phaseMode = 'single',
    mode = 'atap',
    brand = 'goodwe',
    invType = 'string',
    offHourBaselinePct = 0.2,
    fullSelfUse = false,
}) {
    return useMemo(() => {
        const breakdown = deriveSizing({ monthlyBill, tariffRate, operatingDays, maxDemandKw, battKwhTotal, phaseMode, mode });
        if (!monthlyBill || !tariffRate || breakdown.maxPanelsByInv <= 0) {
            return { proposals: { maxSaving: null, fastestRoi: null, selco: null }, breakdown };
        }

        const is3p = phaseMode === '3phase';
        // Explicit master mode (user toggle): ATAP → NEM (export credited),
        // SELCo → zero export (self-consumption only).
        const primaryMode = mode === 'selco' ? 'selco' : 'nem';
        const candidates = [];

        // ATAP iteration honours MD only — 72 kW is a license threshold, not
        // a hard cap. Systems above 72 kW pay the ST License fee, deducted
        // from monthlySaving inside buildCandidate.
        for (let panels = 1; panels <= breakdown.maxPanelsByInv; panels++) {
            const c = buildCandidate({
                panels, brand, invType, is3p, residentialRows,
                monthlyBill, tariffRate, operatingDays, opStart, opEnd, battKwhTotal, breakdown, offHourBaselinePct, fullSelfUse,
                mode: primaryMode,
            });
            if (c) candidates.push(c);
        }

        // SELCo sizing: go to the MAX kWp the inverter supports (inverter × 1.35,
        // = breakdown.maxPanelsByInv). Without a battery the midday excess just
        // curtails; WITH a battery it's stored and used — so the max-kWp build is
        // what makes the battery worthwhile (per user 2026-06-02).
        const selcoOverlapHours = Math.max(0, Math.min(opEnd, SOLAR_END_HR) - Math.max(opStart, SOLAR_START_HR));
        let selco = null;
        if (selcoOverlapHours > 0 && breakdown.maxPanelsByInv > 0) {
            selco = buildCandidate({
                panels: breakdown.maxPanelsByInv, brand, invType, is3p, residentialRows,
                monthlyBill, tariffRate, operatingDays, opStart, opEnd, battKwhTotal, breakdown, offHourBaselinePct, fullSelfUse,
                mode: 'selco',
            });
        }

        if (candidates.length === 0) {
            return { proposals: { maxSaving: null, fastestRoi: null, selco }, breakdown };
        }

        // "Max" picks the SMALLEST INVERTER whose generation already covers the
        // customer's consumption (tie → fewest panels). The component then fills
        // panels to that inverter's max (<1.35 ratio). Choosing the smallest
        // inverter — not the smallest kWp — keeps the inverter from rounding up a
        // 5 kW step and ballooning the fill (e.g. RM350 ≈ 680 kWh → 5 kW / ~6.7 kWp,
        // not a 16 kW / 20 kWp monster).
        const monthlyKwhTarget = (Number(monthlyBill) || 0) / (Number(tariffRate) || 1);
        const covering = candidates.filter(c => (c.generated || 0) >= monthlyKwhTarget);
        // kWp needed to JUST cover consumption, and the inverter that size would
        // NATURALLY want (before the 72 kW cap). If that natural inverter lands in
        // the 72–100 kW dead zone, the customer doesn't truly need a 100 kW plant —
        // cap at the largest 72 kW (≈96 kWp) system instead of jumping to ~129 kWp.
        // (Per user: a 6k bill stays at 96 kWp; only ≥~134 kWp needs / 7k jumps to 100 kW.)
        const genPerKwp = candidates[0]?.kwp > 0 ? candidates[0].generated / candidates[0].kwp : 0;
        const requiredKwp = genPerKwp > 0 ? monthlyKwhTarget / genPerKwp : 0;
        const naturalInv = Math.max(5, Math.ceil(requiredKwp / RATIO_CAP_NO_BATT / 5) * 5);
        const max72 = candidates
            .filter(c => c.inverterKw <= ST_LICENSE_THRESHOLD_KW)
            .reduce((b, c) => (!b || c.kwp > b.kwp ? c : b), null);
        const pickCovering = () => covering.length
            ? covering.reduce((best, c) => (
                c.inverterKw < best.inverterKw ? c
                    : (c.inverterKw === best.inverterKw && c.panels < best.panels ? c : best)))
            : candidates.reduce((best, c) => (c.monthlySaving > best.monthlySaving ? c : best), candidates[0]);
        // Dead zone is now only 73–89 kW (break-free at 90): a natural inverter there
        // caps to the largest 72 kW system (~96 kWp); 90 kW+ breaks free to the real size.
        const maxSaving = (naturalInv > ST_LICENSE_THRESHOLD_KW && naturalInv < ST_BREAKFREE_KW && max72)
            ? max72
            : pickCovering();

        const roiPool = candidates.filter(c => c.savingPct >= 0.5 && c.roiYears != null);
        let fastestRoi = roiPool.length > 0
            ? roiPool.reduce((best, c) => c.roiYears < best.roiYears ? c : best, roiPool[0])
            : null;
        if (fastestRoi && fastestRoi.panels === maxSaving.panels) fastestRoi = null;

        // 50% Saved card: smallest panel count whose savingPct ≥ 0.5. Sized
        // to the budget-conscious "what's the cheapest ATAP that still covers
        // half my bill" question. Often coincides with Fastest ROI in the
        // common linear-saving regime — dedupe so we don't show twins.
        let targetFifty = roiPool.length > 0 ? roiPool[0] : null;
        if (targetFifty && (targetFifty.panels === maxSaving.panels || targetFifty.panels === fastestRoi?.panels)) {
            targetFifty = null;
        }

        // "Snap to 96 kWp" package — 96 kWp on a 72 kW inverter (ratio 1.33, just
        // UNDER the 72 kW ST-license threshold → ST-free). Only offered when the
        // Max Demand can carry a 72 kW inverter (MD ≥ 72). Callers snap a near-96
        // card (kWp ≈ 88–96) to this standard ST-free package.
        let snap96 = null;
        if (maxDemandKw >= 72) {
            snap96 = buildCandidate({
                panels: Math.round(96 / PANEL_KWP), inverterKwOverride: 72,
                brand, invType, is3p, residentialRows,
                monthlyBill, tariffRate, operatingDays, opStart, opEnd, battKwhTotal, breakdown, offHourBaselinePct, fullSelfUse,
                mode: 'nem',
            });
        }

        // Expose the full NEM candidate list so callers can size to an arbitrary
        // bill-saving target (e.g. 50% / 70%). Additive — existing consumers ignore it.
        return { proposals: { maxSaving, fastestRoi, targetFifty, selco }, candidates, snap96, breakdown };
    }, [residentialRows, monthlyBill, tariffRate, operatingDays, opStart, opEnd, maxDemandKw, battKwhTotal, phaseMode, mode, brand, invType, offHourBaselinePct, fullSelfUse]);
}
